import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { 
  ChevronLeft,
  User,
  Lock,
  Bell,
  CreditCard,
  Save,
  Loader2,
  Check,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_new_classes: true,
    email_live_reminders: true,
    email_promotions: false,
    email_weekly_summary: true
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/users/me', profileForm);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.put('/users/me/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);
    try {
      await api.put('/users/me/notifications', notifications);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'subscription', label: 'Subscription', icon: CreditCard }
  ];

  return (
    <div className="min-h-screen bg-[#090D0F]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />
          <Link to="/dashboard">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Settings</h1>

        <div className="grid md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-teal-400/10 text-teal-400' 
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="md:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                <h2 className="text-xl font-bold mb-6">Profile Information</h2>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">First Name</Label>
                      <Input
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                        className="mt-1 bg-[#090D0F] border-zinc-700"
                        data-testid="settings-first-name"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Last Name</Label>
                      <Input
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                        className="mt-1 bg-[#090D0F] border-zinc-700"
                        data-testid="settings-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Email</Label>
                    <Input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                      className="mt-1 bg-[#090D0F] border-zinc-700"
                      disabled
                      data-testid="settings-email"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Contact support to change your email</p>
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-teal-400 text-black font-bold hover:bg-teal-300"
                    data-testid="save-profile-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                </form>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                <h2 className="text-xl font-bold mb-6">Change Password</h2>
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Current Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 pr-10"
                        data-testid="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400">New Password</Label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      className="mt-1 bg-[#090D0F] border-zinc-700"
                      data-testid="new-password"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Confirm New Password</Label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      className="mt-1 bg-[#090D0F] border-zinc-700"
                      data-testid="confirm-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-teal-400 text-black font-bold hover:bg-teal-300"
                    data-testid="update-password-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Update Password</>}
                  </Button>
                </form>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                <h2 className="text-xl font-bold mb-6">Notification Preferences</h2>
                <div className="space-y-4">
                  {[
                    { key: 'email_new_classes', label: 'New Class Alerts', desc: 'Get notified when new classes are added' },
                    { key: 'email_live_reminders', label: 'Live Class Reminders', desc: 'Reminders 30 minutes before your scheduled live classes' },
                    { key: 'email_weekly_summary', label: 'Weekly Summary', desc: 'Weekly recap of your workout stats and achievements' },
                    { key: 'email_promotions', label: 'Promotional Emails', desc: 'Special offers and product announcements' }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-zinc-500">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifications({...notifications, [item.key]: !notifications[item.key]})}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          notifications[item.key] ? 'bg-teal-400' : 'bg-zinc-700'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                          notifications[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={handleNotificationUpdate}
                  disabled={loading}
                  className="mt-6 bg-teal-400 text-black font-bold hover:bg-teal-300"
                  data-testid="save-notifications-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Save Preferences</>}
                </Button>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                <h2 className="text-xl font-bold mb-6">Subscription</h2>
                
                {user?.subscription_status === 'active' ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                      <Check className="w-6 h-6 text-green-500" />
                      <div>
                        <p className="font-bold text-green-500">Active Subscription</p>
                        <p className="text-sm text-zinc-400">You have full access to all classes</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-zinc-800">
                        <span className="text-zinc-400">Plan</span>
                        <span className="font-medium">{user?.subscription_plan || 'Monthly'}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-zinc-800">
                        <span className="text-zinc-400">Next billing date</span>
                        <span className="font-medium">{user?.next_billing_date || 'N/A'}</span>
                      </div>
                    </div>
                    
                    <Link to="/subscription">
                      <Button variant="outline" className="w-full border-zinc-700">
                        Manage Subscription
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Active Subscription</h3>
                    <p className="text-zinc-500 mb-6">Subscribe to unlock unlimited classes</p>
                    <Link to="/subscription">
                      <Button className="bg-teal-400 text-black font-bold hover:bg-teal-300">
                        View Plans
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
